import {
  CallEffect,
  PutEffect,
  all,
  call,
  delay,
  put,
  takeEvery,
  takeLatest,
} from 'redux-saga/effects';
import {
  actions,
  SHOW_TOAST_MESSAGE,
  COMPARE_VERSION,
  HANDLE_ERROR,
  handleError as handleErrorAction,
  setVersionFetchComplete,
} from 'spw-redux/modules/UI/messages/actions';
import {
  INVALID_CREDENTIALS,
  NOT_AUTHENTICATED,
  SESSION_EXPIRED,
  SESSION_SUPERSEDED,
  USER_NOT_FOUND,
} from 'spw-constants';
import { fetchVersion } from 'spw-api';
import { APIResponse } from 'spw-redux/types';
import { logout as logoutAction } from 'spw-redux/modules/auth/actions';
import { versionProvider } from 'spw-shared/version';
import * as Sentry from '@sentry/react';
import * as messagesActions from './actions';
import { LogoutReason } from './types';

function* showToastMessage(
  action: ReturnType<typeof actions.showToastMessage>
) {
  try {
    yield delay(action.payload.timeout);

    yield put(messagesActions.actions.hideToastMessage());
  } catch (e) {
    yield put(messagesActions.actions.handleError(e));
  }
}

function* handleError(action: ReturnType<typeof actions.handleError>) {
  const { error } = action.payload;
  let message = error;

  if (error?.toString) {
    message = error.toString();
  }

  if (message === SESSION_SUPERSEDED) {
    yield put(
      messagesActions.showPermanentLogoutMessage(LogoutReason.sessionSuperseded)
    );
    yield put(logoutAction());
    return;
  }
  if (message === SESSION_EXPIRED) {
    yield put(
      messagesActions.showPermanentLogoutMessage(LogoutReason.sessionExpired)
    );
    yield put(logoutAction());
    return;
  }
  if (
    message === INVALID_CREDENTIALS ||
    message?.includes('401') ||
    message === NOT_AUTHENTICATED
  ) {
    yield put(logoutAction());
    return;
  }
  if (message === USER_NOT_FOUND) {
    yield put(
      actions.showToastMessage({ text: 'User not found', status: 'error' })
    );
    yield put(logoutAction());
    return;
  }

  // handle exceptions here
  yield put(
    actions.showToastMessage({
      text: 'The Wizard has been notified and is working on the issue.',
      status: 'error',
    })
  );

  // Network errors are already being reported to sentry from within the apollo client instance
  if (!error?.networkError?.statusCode) {
    Sentry.captureException(error);
  }
}

function* compareVersion(): Generator<
  | CallEffect<APIResponse<typeof fetchVersion>>
  | PutEffect<ReturnType<typeof setVersionFetchComplete>>
  | PutEffect<ReturnType<typeof handleErrorAction>>,
  void,
  APIResponse<typeof fetchVersion>
> {
  try {
    const res: APIResponse<typeof fetchVersion> = yield call(fetchVersion);
    const {
      data: { currentVersion },
    } = res;

    const persistedVersion = versionProvider.getVersion();
    yield put(setVersionFetchComplete());

    if (persistedVersion !== currentVersion) {
      versionProvider.setVersion(currentVersion);
      const searchParams = new URLSearchParams(window.location.search);
      searchParams.set('upd', currentVersion);
      const newSearch = searchParams.toString();

      window.location.href = `${window.location.pathname}?${newSearch}`;
    }
  } catch (e) {
    yield put(handleErrorAction(e));
  }
}

function* saga() {
  yield all([
    takeLatest(SHOW_TOAST_MESSAGE, showToastMessage),
    takeEvery(HANDLE_ERROR, handleError),
    takeEvery(COMPARE_VERSION, compareVersion),
  ]);
}

export default saga;
